/**
 * Authentication Service
 * Handles JWT token storage and retrieval for API calls
 */

import logger from '../utils/logger';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user_data';

export const authService = {
  /**
   * Store JWT token and user data after successful login
   * @param {string} token - JWT access token
   * @param {object} user - User data object
   */
  setAuthData(token, user = null) {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }
    }
  },

  /**
   * Get stored JWT token
   * @returns {string|null} JWT token or null if not found
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Get stored user data
   * @returns {object|null} User object or null if not found
   */
  getUser() {
    const userData = localStorage.getItem(USER_KEY);
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (e) {
        logger.error('Error parsing user data:', e);
        return null;
      }
    }
    return null;
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if token exists
   */
  isAuthenticated() {
    return !!this.getToken();
  },

  /**
   * Clear all auth data (logout)
   */
  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Login to backend and store JWT
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<object>} Login response with token and user data
   */
  async login(email, password) {
    // CRITICAL: Use .trim() to remove trailing newlines from Vercel env vars
    const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
    const isTestEnv =
      env.MODE === 'test' ||
      env.VITEST === 'true' ||
      env.VITEST === true ||
      (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test');
    const baseURL = (
      (isTestEnv ? 'http://localhost:8000' : null) ||
      env.VITE_API_BASE_URL ||
      env.VITE_API_URL ||
      'https://apps.quantrolix.ai/api/quantrolix'
    ).trim();

    try {
      const response = await fetch(`${baseURL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(error.detail || 'Invalid credentials');
      }

      const data = await response.json();

      // Store the JWT token and user data
      if (data.access_token) {
        this.setAuthData(data.access_token, data.user);
      }

      return data;
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Sign up new user and store JWT
   * @param {object} userData - User registration data
   * @returns {Promise<object>} Signup response with token and user data
   */
  async signup(userData) {
    // CRITICAL: Use .trim() to remove trailing newlines from Vercel env vars
    const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
    const isTestEnv =
      env.MODE === 'test' ||
      env.VITEST === 'true' ||
      env.VITEST === true ||
      (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test');
    const baseURL = (
      (isTestEnv ? 'http://localhost:8000' : null) ||
      env.VITE_API_BASE_URL ||
      env.VITE_API_URL ||
      'https://apps.quantrolix.ai/api/quantrolix'
    ).trim();

    try {
      const response = await fetch(`${baseURL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Signup failed' }));
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();

      // Store the JWT token and user data
      if (data.access_token) {
        this.setAuthData(data.access_token, data.user);
      }

      return data;
    } catch (error) {
      logger.error('Signup error:', error);
      throw error;
    }
  },

  /**
   * Refresh JWT token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<object>} New access token
   */
  async refreshToken(refreshToken) {
    // CRITICAL: Use .trim() to remove trailing newlines from Vercel env vars
    const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
    const isTestEnv =
      env.MODE === 'test' ||
      env.VITEST === 'true' ||
      env.VITEST === true ||
      (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test');
    const baseURL = (
      (isTestEnv ? 'http://localhost:8000' : null) ||
      env.VITE_API_BASE_URL ||
      env.VITE_API_URL ||
      'https://apps.quantrolix.ai/api/quantrolix'
    ).trim();

    try {
      const response = await fetch(`${baseURL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Update the stored token
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
      }

      return data;
    } catch (error) {
      logger.error('Token refresh error:', error);
      // If refresh fails, logout the user
      this.logout();
      throw error;
    }
  },
};

export default authService;
